import * as React from 'react'

import {
  BehindScenes,
  Callout,
  Flow,
  HowToUse,
  KVTable,
  PageMeta,
  StepList,
  Tag,
} from '@/components/docs/primitives'
import { H3, P } from '@/components/docs/prose'
import { brand } from '@/brand/brand'

export function DocCreateImage() {
  return (
    <>
      <PageMeta
        title="Create Image"
        summary={`Generate hero images — avatars, product styling variations, and composites of a person holding your product bottle. These images become reference material for video ads or stand-alone creative.`}
      />

      <HowToUse>
        <H3>Avatar tab</H3>
        <P>
          Use this tab to generate variations of a spokesperson or customer persona —
          for example, the senior woman who gives a heartfelt Novera Radiance Serum testimonial,
          or the everyday customer who reached for Novera Body Oil after years of dry skin. You
          can start from a portrait photo you already have, or describe someone from scratch.
        </P>
        <StepList
          items={[
            {
              title: 'Upload a reference portrait or select one from your avatar library',
              body: 'Drag and drop a photo, click to browse, or pick an existing asset from the grid. As soon as an image loads, the system analyses it and fills in a description, feature tags, and a suggested generation prompt.',
            },
            {
              title: 'Give the avatar a name',
              body: 'This name is stored with every image saved from this session, making it easy to find later — for example "Senior Radiance Customer" or "Lifestyle Model A".',
            },
            {
              title: 'Review and edit the suggested prompt',
              body: 'The analysis populates the Generation prompt field. Edit it freely — change the background setting, lighting mood, or clothing. Use the Additional instructions field for short style notes such as "warm kitchen window light, natural expression". Use Describe changes to apply targeted tweaks on top of the current description.',
            },
            {
              title: 'Choose model, aspect ratio, quality, and number of variations',
              body: 'Nano Banana Fast is the default and is well-suited to quick rounds of variation. Nano Banana Pro produces noticeably crisper facial detail and is worth using when the image will become a reference portrait for a talking-head video. Quality levels are 512 (fast preview), 1K (default), 2K (hero-ready), and 4K (sharpest, slowest). Variations run 1 to 5.',
            },
            {
              title: 'Generate and save',
              body: 'Hit Generate. Results appear as a grid of thumbnails — generation is near-instant. Toggle the variations you want, pick or name a folder, and click Save to Library.',
            },
          ]}
        />

        <H3>Product tab</H3>
        <P>
          Use this tab to generate styling variations of an existing product. The same
          Novera Body Oil bottle photographed on marble, on linen, on obsidian, at golden hour —
          without booking a photoshoot. The output fills your saved context with new hero shots
          for ad references and organic social.
        </P>
        <StepList
          items={[
            {
              title: 'Select the product image',
              body: 'Upload a photo or pick one from your Asset Library. Images stored under your product folders appear first. Once you select an image, the system analyses it and extracts a product description and feature tags.',
            },
            {
              title: 'Review the AI analysis',
              body: 'The analysis step shows the detected product description and tags. These are automatically combined with your variation text to form the full generation prompt. Check the detected name and label text are correct before continuing.',
            },
            {
              title: 'Describe the variation',
              body: 'Write a short scene description — for example, "Novera Body Oil bottle on a warm marble surface with fresh rosemary and almond blossoms scattered around it, golden afternoon light from the left" or "flat lay with Novera Radiance Serum surrounded by rosehip petals on a dark slate surface". Leave it blank to regenerate the product in a neutral setting.',
            },
            {
              title: 'Choose model, aspect ratio, quality, and variations',
              body: 'Same controls as the Avatar tab. 1K quality is recommended for most product shots. Use 2K for anything going into a final ad creative.',
            },
            {
              title: 'Generate and save selected images',
              body: 'Click Generate. Review the grid, select the variations to keep, choose or create a folder, and save.',
            },
          ]}
        />

        <H3>Composite tab</H3>
        <P>
          Use this tab to combine a person and a product into a single image — a senior
          woman holding Radiance Serum at her vanity, a doctor pointing the Novera Hair Revival Kit toward
          the camera, a woman applying Shower Oil in a warm bathroom. This is how you produce hero
          visuals for ads that include both a spokesperson and the actual product bottle, without
          a photoshoot.
        </P>
        <StepList
          items={[
            {
              title: 'Pick the product',
              body: 'Choose from your product library using the dropdown, which pre-loads the linked product images. You can toggle up to three angles — hero, three-quarter, and label close-up — to include as references. Or upload an image directly from your device if you are working outside the product library.',
            },
            {
              title: 'Pick the avatar',
              body: 'Upload a portrait from your device or click any image in the avatar library grid. The avatar you pick is passed to the AI as a reference so the composite preserves the person\'s appearance — skin tone, hair, face shape.',
            },
            {
              title: 'Write Scene Instructions (optional)',
              body: 'Describe the pose, setting, and framing. For example: "Doctor seated at a clinical desk, holding the Novera Radiance Serum label toward camera, warm studio lighting, white coat" or "Senior woman at her bathroom vanity applying Novera Body Oil to her forearm, soft morning light, calm expression". Without instructions, the AI defaults to a studio-lit product-holding shot.',
            },
            {
              title: 'Choose variations, aspect ratio, and quality',
              body: '512 is fast and cheap, good for quick concept iteration. 1K is the default and appropriate for most composites. 2K is noticeably crisper — use it for anything going into a final ad creative.',
            },
            {
              title: 'Generate and save',
              body: 'Click Generate. Results appear as a grid. Toggle the variations you want to keep, pick or create a folder, and click Save. Each saved image is automatically analysed so it becomes searchable in your Asset Library.',
            },
          ]}
        />

        <Callout variant="tip">
          Scene instructions are the fastest way to get a specific composite. Without them you
          get a sensible studio shot. With them you can get "doctor at a clinical desk pointing
          the bottle toward camera" or "woman in a dim bathroom pressing Novera Radiance Serum into her
          cheek by candlelight". Write one sentence of scene direction and the AI builds the rest.
        </Callout>

        <H3>Storyboard tab</H3>
        <P>
          Use this tab to plan a multi-scene ad as a printable storyboard before you generate the
          actual video. It produces a single grid image of every scene with composition, blocking,
          palette, and lighting locked in — then you click "Use in Create Ad" and the storyboard
          becomes a Seedance reference that anchors every scene of the video.
        </P>
        <StepList
          items={[
            {
              title: 'Write a brief',
              body: 'Two to four sentences describing the ad — talent, setting, product, tone. The LLM expands this into a structured panel-by-panel plan.',
            },
            {
              title: 'Pick a panel count',
              body: 'How many panels the storyboard renders. Default is 6 panels = 6 scenes (one per panel). Toggle "Start + end frames per scene" to switch to pair mode — each scene gets a start frame AND an end frame, capping at 10 panels = 5 scenes.',
            },
            {
              title: 'Pick a render style',
              body: 'Illustration (default) — line + watercolor wash designer storyboard with stylized people. Safe to feed Seedance later because drawn faces don\'t trigger face moderation. Standalone — photoreal panels with no visible faces (bodies / hands / silhouettes only). Use for product-only ads. With Face — photoreal panels with real faces shown. Client sign-off deliverable; Seedance face moderation may reject it as a video ref.',
            },
            {
              title: 'Attach references (optional)',
              body: 'Up to 4 image references — your logo for the end card, an inspiration shot for palette, an existing product photo. Describe each one so the LLM knows how to use it. You can also pick from your library or upload fresh.',
            },
            {
              title: 'Generate and use in Create Ad',
              body: 'The plan generates in ~10-15s; the visual board renders in ~60-120s. When it lands, click "Use in Create Ad" — the storyboard becomes the attached reference for your video script generation.',
            },
          ]}
        />

        <Callout variant="tip">
          For human-centered ads use the default <Tag>Illustration</Tag> render style. Drawn faces
          stay Seedance-safe AND carry composition + body language + blocking that the video
          pipeline can anchor to. The video output is always photoreal even when the storyboard
          is illustrated — the storyboard is a planning artifact, never the final aesthetic.
        </Callout>

        <Callout variant="tip">
          <strong>Start + end frames per scene</strong> shines when you need precise visual anchors
          for each scene — for example a hero product reveal arc where scene 2 opens on the bottle
          on a vanity and closes on hands picking it up. With this toggle ON, panel 3 is "Scene 2
          · start" and panel 4 is "Scene 2 · end" — both feed Seedance as composition refs.
        </Callout>
      </HowToUse>

      <BehindScenes>
        <P>
          Image generation in {brand.productName} is near-instant — unlike video ads, which
          render as background jobs. The page waits for the AI to return the image and then
          displays the results directly. This is why Create Image feels immediate compared to
          Create Ad.
        </P>

        <H3>Composite prompt anatomy</H3>
        <P>
          When you hit Generate on the Composite tab, the AI receives a prompt assembled from
          four parts, in this order:
        </P>
        <StepList
          items={[
            {
              title: 'Avatar description',
              body: 'The AI-generated visual description of the person you picked — for example, "senior woman with short silver hair and warm brown eyes, wearing a cream linen shirt, calm confident expression". This anchors the person\'s appearance across all variations.',
            },
            {
              title: 'Product name and benefits',
              body: 'The product name and its key benefits pulled from your product library — for example, "Novera Radiance Serum — nourishes, hydrates, and smooths with rosehip and vitamin C". This tells the AI what is in the person\'s hand and why.',
            },
            {
              title: 'Your scene instructions',
              body: 'Your text verbatim — the pose, setting, and framing you described. If you left this blank, a default template is used that places the avatar in a neutral setting with the product clearly visible and the label facing camera.',
            },
            {
              title: 'Quality anchors',
              body: '"Product label clearly visible, studio lighting, sharp focus on both person and product." These are appended automatically to every composite prompt to keep label legibility and person sharpness consistent.',
            },
          ]}
        />

        <H3>Why multi-angle product references matter</H3>
        <Callout variant="tip">
          With your gold-embossed labels on amber glass, a single hero image is not enough.
          The AI has to guess the label text and packaging geometry from one angle. Three angles —
          front, three-quarter, and label close-up — let it triangulate the real lettering and
          preserve it faithfully in the composite. This is the single biggest quality lever for
          label accuracy in composites. Always use three angles when the product label is visible
          in the intended shot.
        </Callout>

        <H3>Flash vs Pro</H3>
        <P>
          Nano Banana Fast is well-suited to quick rounds of exploration — generating ten
          variations of a spokesperson at low cost to find the right look before committing to a
          final render. Nano Banana Pro is slower but noticeably crisper on fine detail: label
          text, embossing, facial texture, fabric. Use Pro for any composite or product image
          that is going into a live ad or being passed as a reference image into a Create Ad run.
        </P>

        <H3>Quality levels</H3>
        <KVTable
          rows={[
            {
              key: '512',
              value:
                'Fast preview. Good for checking composition, pose, and general framing before committing to a higher-quality render.',
            },
            {
              key: '1K',
              value:
                'Default. Appropriate for most saved-context additions, social posts, and ad reference images where you are not planning to crop into fine detail.',
            },
            {
              key: '2K',
              value:
                'Hero-ready. Use for anything going into a final ad creative, a product page image, or a high-res reference for a video render. Labels and textures are noticeably sharper.',
            },
          ]}
        />

        <H3>What gets saved</H3>
        <P>
          When you select variations and click Save, the chosen images are moved into the library
          folder you picked or named. Each saved image is automatically analysed — the AI reads
          the image and writes a description, assigns tags, and makes the image searchable in your
          Asset Library. This means a composite of a woman holding Radiance Serum will surface
          when you search for "Radiance" or "composite" or "serum" later, without any manual
          tagging.
        </P>

        <Flow
          steps={[
            'You select avatar, product images, and optional scene instructions',
            'The AI assembles the composite prompt: avatar description + product benefits + your scene instructions + quality anchors',
            'Avatar image and up to three product images are passed as references',
            'The AI generates N variations and returns them for review',
            'You select the variations to keep and click Save',
            'Selected images move to your chosen library folder',
            'AI analysis runs on each saved image — tags written for search',
          ]}
        />
      </BehindScenes>
    </>
  )
}
